import React, { useEffect, useState } from 'react';
import {
  TextField,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Autocomplete,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Attachment,
  ItemWithRelations,
  useGetItemLocationsQuery,
  useGetItemsDropdownQuery,
} from '../../../redux/vmsApi';
import {
  useCreateContractItemMutation,
  useGetProcurementContractByIdQuery,
  useUpdateContractItemByIdMutation,
} from '../../../redux/procurementContractsApi';
import { getVatRate } from '../../../utils/vatUtil';
import DropzoneArea from '../../common/DropzoneArea';

const VAT_RATE = getVatRate(new Date());

interface CreateContractItemComponentProps {
  contractId: string;
  editingItem?: any;
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
}

const validationSchema = Yup.object({
  itemCode: Yup.string().required('Item code is required'),
  itemDescription: Yup.string(),
  itemUnit: Yup.string(),
  itemBudgetedCost: Yup.number().required('Rate is required').min(0),
  itemQuantity: Yup.number().required('Quantity is required').min(0),
  isService: Yup.boolean(),
  isVAT: Yup.boolean(),
});

const CreateContractItemComponent: React.FC<CreateContractItemComponentProps> = ({
  contractId,
  editingItem,
  onClose,
  onSuccess,
  open,
}) => {
  const { data: contractDetails } = useGetProcurementContractByIdQuery({ id: contractId }, { skip: !contractId });
  const { data: inventoryItems = [] } = useGetItemsDropdownQuery();
  const [postContractItem, { isLoading: isPosting }] = useCreateContractItemMutation();
  const [patchContractItem, { isLoading: isPatching }] = useUpdateContractItemByIdMutation();
  const [error, setError] = useState<string | null>(null);

  const contractName = contractDetails?.title || editingItem?.contractName || '';
  const supplierName = contractDetails?.supplier?.name || editingItem?.supplierName || '';

  const formik = useFormik({
    initialValues: {
      itemCode: editingItem?.itemCode || '',
      itemDescription: editingItem?.itemDescription || '',
      itemUnit: editingItem?.itemUnit || '',
      itemBudgetedCost: editingItem?.itemBudgetedCost || '',
      itemQuantity: editingItem?.itemQuantity || '',
      isService: editingItem?.isService ?? false,
      isVAT: false,
      auditComment: '',
      attachments: [] as Attachment[],
    },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        setError(null);
        const enteredRate = parseFloat(values.itemBudgetedCost as unknown as string);
        const itemQuantity = parseFloat(values.itemQuantity as unknown as string);
        const itemBudgetedCost = values.isVAT ? enteredRate / (1 + VAT_RATE) : enteredRate;
        const itemCurrentValue = itemBudgetedCost * itemQuantity;

        if (editingItem?.id) {
          if (!values.auditComment?.trim()) {
            setError('Please enter a comment to justify this contract item adjustment.');
            return;
          }

          await patchContractItem({
            id: editingItem.id,
            body: {
              ...values,
              auditComment: values.auditComment,
              isVAT: !values.isVAT,
              contractName,
              supplierName,
              itemBudgetedCost,
              itemQuantity,
              itemCurrentValue,
              initialQuantity: itemQuantity,
              itemInitialValue: itemCurrentValue,
              attachments: values.attachments ?? [],
            },
          }).unwrap();
        } else {
          await postContractItem({
            newContractItem: {
              contractId,
              ...values,
              isVAT: !values.isVAT,
              contractName,
              supplierName,
              itemBudgetedCost,
              itemQuantity,
              itemCurrentValue,
              initialQuantity: itemQuantity,
              itemInitialValue: itemCurrentValue,
              attachments: values.attachments ?? [],
            } as never,
          }).unwrap();
        }
        formik.resetForm();
        onSuccess();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to save contract item');
      }
    },
  });

  const trimmedDescription = formik.values.itemDescription?.trim() || '';
  const isSv024Code = formik.values.itemCode === 'SV024';
  const shouldFilterByDescription = isSv024Code && !!trimmedDescription;

  const { data: matchedItemLocations = [] } = useGetItemLocationsQuery(
    {
      itemCode: formik.values.itemCode || undefined,
      itemDescription: shouldFilterByDescription ? trimmedDescription : undefined,
      limit: shouldFilterByDescription ? 25 : 1,
      skip: 0,
    },
    {
      skip: !formik.values.itemCode || (isSv024Code && !trimmedDescription),
    }
  );

  const latestMatchingLocation = matchedItemLocations[0];

  useEffect(() => {
    if (!latestMatchingLocation) {
      return;
    }

    const totalRate = Number(latestMatchingLocation.rate || 0) + Number(latestMatchingLocation.VAT || 0);
    if (!Number.isFinite(totalRate)) {
      return;
    }

    formik.setFieldValue('itemBudgetedCost', totalRate.toFixed(2), false);
  }, [
    latestMatchingLocation?.id,
    latestMatchingLocation?.rate,
    latestMatchingLocation?.VAT,
  ]);

  const handleAutocompleteChange = (newValue: ItemWithRelations | null) => {
    formik.setFieldValue('itemCode', newValue?.itemCode || '');
    formik.setFieldValue('itemDescription', newValue?.description || '');
    formik.setFieldValue('itemUnit', newValue?.quantityUnit || '');
    formik.setFieldValue('isService', newValue?.isService ?? false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 700, color: '#1976d2' }}>
        {editingItem?.id ? 'Edit Contract Item' : 'Create New Contract Item'}
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField label="Contract" fullWidth value={contractName} disabled />

          <TextField label="Supplier" fullWidth value={supplierName} disabled />

          <Autocomplete
            options={inventoryItems}
            getOptionLabel={(option) => `${option.itemCode} - ${option.description}`}
            value={inventoryItems.find((item) => item.itemCode === formik.values.itemCode) || null}
            onChange={(_event, newValue) => handleAutocompleteChange(newValue)}
            renderInput={(params) => (
              <TextField {...params} label="Search Inventory Item" placeholder="Select an inventory item" fullWidth />
            )}
          />

          <TextField
            label="Item Code"
            fullWidth
            name="itemCode"
            value={formik.values.itemCode}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            error={formik.touched.itemCode && !!formik.errors.itemCode}
            helperText={
              formik.touched.itemCode && typeof formik.errors.itemCode === 'string' ? formik.errors.itemCode : ''
            }
            required
          />

          <TextField
            label="Description"
            fullWidth
            name="itemDescription"
            value={formik.values.itemDescription}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            multiline
            rows={2}
          />

          {editingItem?.id && (
            <TextField
              label="Adjustment Comment"
              fullWidth
              name="auditComment"
              value={(formik.values as any).auditComment || ''}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              required
              multiline
              rows={2}
              placeholder="Enter reason for quantity/rate/value adjustment"
            />
          )}

          <Box>
            <Box sx={{ mb: 1, fontSize: 14, fontWeight: 500 }}>Supporting Documents (optional)</Box>
            <DropzoneArea
              fieldName="attachments"
              attachments={formik.values.attachments as Attachment[]}
              setFieldValue={formik.setFieldValue}
            />
          </Box>

          <TextField
            label="Unit"
            fullWidth
            name="itemUnit"
            value={formik.values.itemUnit}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled
            placeholder="e.g., pcs, kg, m2"
          />

          <TextField
            label="Rate"
            fullWidth
            name="itemBudgetedCost"
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
            value={formik.values.itemBudgetedCost}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.itemBudgetedCost && !!formik.errors.itemBudgetedCost}
            helperText={
              formik.touched.itemBudgetedCost && typeof formik.errors.itemBudgetedCost === 'string'
                ? formik.errors.itemBudgetedCost
                : ''
            }
            required
          />

          <TextField
            label="Quantity"
            fullWidth
            name="itemQuantity"
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
            value={formik.values.itemQuantity}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.itemQuantity && !!formik.errors.itemQuantity}
            helperText={
              formik.touched.itemQuantity && typeof formik.errors.itemQuantity === 'string'
                ? formik.errors.itemQuantity
                : ''
            }
            required
          />

          <FormControlLabel
            control={<Checkbox name="isService" checked={formik.values.isService} onChange={formik.handleChange} />}
            label="Is Service"
          />

          <FormControlLabel
            control={<Checkbox name="isVAT" checked={formik.values.isVAT} onChange={formik.handleChange} />}
            label="Remove VAT (entered price is VAT inclusive)"
          />
          {formik.values.isVAT && !!formik.values.itemBudgetedCost && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Pre-VAT rate: <strong>{(parseFloat(formik.values.itemBudgetedCost as unknown as string) / (1 + VAT_RATE)).toFixed(2)}</strong>
              &nbsp;| VAT portion: <strong>{(parseFloat(formik.values.itemBudgetedCost as unknown as string) - parseFloat(formik.values.itemBudgetedCost as unknown as string) / (1 + VAT_RATE)).toFixed(2)}</strong>
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPosting || isPatching}>
          Cancel
        </Button>
        <Button
          onClick={() => formik.handleSubmit()}
          variant="contained"
          disabled={isPosting || isPatching || !formik.isValid}
          sx={{ bgcolor: '#1976d2', '&:hover': { bgcolor: '#125ea3' } }}
        >
          {isPosting || isPatching ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Saving...
            </>
          ) : (
            'Save'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateContractItemComponent;
